import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { useTenantStore } from '../../../stores/tenant.store';
import { useAcademic } from '../../../hooks/use-academic';
import { DocumentData } from 'firebase/firestore';

interface DeleteProgramDialogProps {
  open: boolean;
  onClose: () => void;
  program: DocumentData | null;
}

export function DeleteProgramDialog({ open, onClose, program }: DeleteProgramDialogProps) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const { deleteProgram, isDeletingProgram } = useAcademic();
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!currentTenantId || !program) return;
    setError(null);
    try {
      await deleteProgram({
        tenantId: currentTenantId,
        programId: program.id,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error deleting program');
    }
  };

  if (!program) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('programDialogs.delete.title')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography>
          <Trans
            i18nKey="programDialogs.delete.confirm"
            t={t}
            values={{ name: program.name, code: program.code }}
            components={{ strong: <strong /> }}
          />
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isDeletingProgram}>
          {t('programDialogs.delete.actions.cancel')}
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={isDeletingProgram}
        >
          {isDeletingProgram
            ? t('programDialogs.delete.actions.deleting')
            : t('programDialogs.delete.actions.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
